import React, { useState, useEffect, useCallback } from 'react';
import { RiAddLine, RiStackLine, RiTeamLine } from 'react-icons/ri';
import styles from '../styles/AdminPage.module.css';
import { buildAgentCards, buildUsageTotals } from '../utils/agentAnalytics';

const BASE = process.env.REACT_APP_AUTH_BACKEND_URL;

const IconH4 = ({ icon: Icon, children }) => (
  <h4 style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '0 0 10px' }}>
    <Icon style={{ flexShrink: 0 }} />
    {children}
  </h4>
);


export default function UsageDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [swarmAnalytics, setSwarmAnalytics] = useState(null);
  const [workflowAnalytics, setWorkflowAnalytics] = useState(null);
  const [months, setMonths] = useState([]);
  const [period, setPeriod] = useState('lifetime');
  const [selectedYear, setSelectedYear] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      try {
        const [aRes, mRes, sRes, wRes] = await Promise.all([
          fetch(`${BASE}/api/user/usage`, { credentials: 'include' }),
          fetch(`${BASE}/api/user/usage/months`, { credentials: 'include' }),
          fetch(`${BASE}/api/user/swarm-usage`, { credentials: 'include' }),
          fetch(`${BASE}/api/user/workflow-usage`, { credentials: 'include' }),
        ]);
        if (cancelled) return;
        if (aRes.ok) setAnalytics((await aRes.json()).analytics);
        if (mRes.ok) setMonths((await mRes.json()).months || []);
        if (sRes.ok) setSwarmAnalytics((await sRes.json()).swarm_usage);
        if (wRes.ok) setWorkflowAnalytics((await wRes.json()).workflow_usage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, []);

  const handlePeriodChange = useCallback(async (newPeriod) => {
    setPeriod(newPeriod);
    const url = newPeriod === 'lifetime'
      ? `${BASE}/api/user/usage`
      : `${BASE}/api/user/usage?period=${newPeriod}`;
    const res = await fetch(url, { credentials: 'include' });
    if (res.ok) setAnalytics((await res.json()).analytics);
  }, []);

  // Grand total computation (Ask + Swarm + Workflow)
  const _orchTok = analytics?.tokens || { input: 0, output: 0, total: 0 };
  const _swarmTok = swarmAnalytics?.tokens || { input: 0, output: 0, total: 0 };
  const _wfTok = workflowAnalytics?.tokens || { input: 0, output: 0, total: 0 };
  const grandTokens = {
    input: _orchTok.input + _swarmTok.input + _wfTok.input,
    output: _orchTok.output + _swarmTok.output + _wfTok.output,
    total: _orchTok.total + _swarmTok.total + _wfTok.total,
  };
  const { models: grandModels, providers: grandProviders } = buildUsageTotals(
    analytics,
    swarmAnalytics,
    workflowAnalytics,
  );

  const showGrandTotal = viewMode === 'grand-total' || viewMode === 'all';
  const showOrch = viewMode === 'all' || viewMode === 'orchestration';
  const showSwarm = viewMode === 'all' || viewMode === 'swarm';
  const showWorkflow = viewMode === 'all' || viewMode === 'workflow';
  const showAgents = showOrch || showWorkflow;

  // Unified agent cards: one card per agent type, merging Ask + Workflow usage
  const agentCards = buildAgentCards(analytics, workflowAnalytics);

  // Period selector helpers
  const monthsByYear = months.reduce((acc, m) => {
    const y = String(m.year);
    if (!acc[y]) acc[y] = [];
    acc[y].push(m);
    return acc;
  }, {});
  const availableYears = Object.keys(monthsByYear).sort((a, b) => b - a);
  const derivedYear = period !== 'lifetime' ? period.split('-')[0] : availableYears[0];
  const activeYear = selectedYear || derivedYear;
  const monthsForYear = (monthsByYear[activeYear] || []).slice().sort((a, b) => b.month - a.month);
  const activeMonthKey = period !== 'lifetime' && period.startsWith(activeYear || '') ? period : '';
  const activePeriodLabel = period === 'lifetime'
    ? 'Lifetime cumulative totals'
    : months.find(m => `${m.year}-${String(m.month).padStart(2, '0')}` === period)?.label || period;

  const selectStyle = {
    padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem',
    border: '1px solid var(--border-color, #ccc)',
    background: 'var(--input-background, #fff)',
    color: 'var(--text-color, #333)', cursor: 'pointer',
  };

  return (
    <div style={{ padding: '24px 28px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '1.4rem' }}>My Usage</h2>

        <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} style={selectStyle}>
          <option value="all">All</option>
          <option value="grand-total">Grand Total</option>
          <option value="orchestration">Orchestration</option>
          <option value="swarm">Swarm</option>
          <option value="workflow">Workflow</option>
        </select>

        {/* Period selector — only shown when orchestration data is relevant */}
        {viewMode !== 'swarm' && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => { setSelectedYear(null); handlePeriodChange('lifetime'); }}
              style={{
                ...selectStyle,
                fontWeight: period === 'lifetime' ? 600 : 400,
                background: period === 'lifetime' ? 'var(--link-color, #4a9eff)' : selectStyle.background,
                color: period === 'lifetime' ? '#fff' : selectStyle.color,
                border: period === 'lifetime' ? '1px solid transparent' : selectStyle.border,
              }}
            >
              Lifetime
            </button>
            {availableYears.length > 0 && (
              <select
                value={activeYear || ''}
                style={selectStyle}
                onChange={(e) => {
                  const year = e.target.value;
                  setSelectedYear(year);
                  const first = (monthsByYear[year] || []).slice().sort((a, b) => b.month - a.month)[0];
                  if (first) handlePeriodChange(`${first.year}-${String(first.month).padStart(2, '0')}`);
                }}
              >
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {monthsForYear.length > 0 && (
              <select
                value={activeMonthKey}
                style={selectStyle}
                onChange={(e) => handlePeriodChange(e.target.value)}
              >
                <option value="" disabled>Month</option>
                {monthsForYear.map(m => {
                  const key = `${m.year}-${String(m.month).padStart(2, '0')}`;
                  return <option key={key} value={key}>{m.label}</option>;
                })}
              </select>
            )}
          </div>
        )}

        <span style={{ fontSize: '0.82em', color: 'var(--text-secondary, #888)', marginLeft: 'auto' }}>
          {activePeriodLabel}
        </span>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary, #888)' }}>
          Loading usage data…
        </div>
      ) : !analytics ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary, #888)' }}>
          No usage data yet. Start chatting to see your stats here.
        </div>
      ) : (
        <div className={styles.analyticsGrid}>

          {/* ── Grand Total ── */}
          {showGrandTotal && <>
            <div className={styles.analyticsSection}>
              <IconH4 icon={RiAddLine}>Activity</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Ask Queries:</span><span className={styles.statValue}>{analytics.total_queries ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Ask Conversations:</span><span className={styles.statValue}>{analytics.total_conversations ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Web Searches:</span><span className={styles.statValue}>{analytics.web_search_count ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Swarm Sessions:</span><span className={styles.statValue}>{swarmAnalytics?.total_sessions ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Swarm Turns:</span><span className={styles.statValue}>{swarmAnalytics?.total_turns ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Workflow Runs:</span><span className={styles.statValue}>{workflowAnalytics?.total_runs ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Workflow Completed:</span><span className={styles.statValue}>{workflowAnalytics?.completed_runs ?? 0}</span></div>
              </div>
            </div>

            <div className={styles.analyticsSection}>
              <IconH4 icon={RiAddLine}>Total Token Usage</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Input Tokens:</span><span className={styles.statValue}>{grandTokens.input.toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Output Tokens:</span><span className={styles.statValue}>{grandTokens.output.toLocaleString()}</span></div>
                <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}><span className={styles.statLabel}>Total Tokens:</span><span className={styles.statValue}>{grandTokens.total.toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Orch Tokens:</span><span className={styles.statValue}>{_orchTok.total.toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Swarm Tokens:</span><span className={styles.statValue}>{_swarmTok.total.toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Workflow Tokens:</span><span className={styles.statValue}>{_wfTok.total.toLocaleString()}</span></div>
              </div>
            </div>

            {Object.keys(grandModels).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiAddLine}>Models Used</IconH4>
                <div className={styles.modelList}>
                  {Object.entries(grandModels).sort(([, a], [, b]) => b - a).map(([model, count]) => (
                    <div key={model} className={styles.modelItem}>
                      <span className={styles.modelName}>{model}</span>
                      <span className={styles.modelCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(grandProviders).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiAddLine}>Providers</IconH4>
                <div className={styles.providerList}>
                  {Object.entries(grandProviders).sort(([, a], [, b]) => b - a).map(([provider, count]) => (
                    <div key={provider} className={styles.providerItem}>
                      <span className={styles.providerName}>{provider}</span>
                      <span className={styles.providerCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>}

          {/* ── Ask ── */}
          {showOrch && <>
            <div className={styles.analyticsSection}>
              <IconH4 icon={RiStackLine}>Ask Overview</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Total Queries:</span><span className={styles.statValue}>{analytics.total_queries}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Conversations:</span><span className={styles.statValue}>{analytics.total_conversations}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Web Searches:</span><span className={styles.statValue}>{analytics.web_search_count}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Concise Only:</span><span className={styles.statValue}>{analytics.concise_calls ?? 0}</span></div>
              </div>
            </div>

            <div className={styles.analyticsSection}>
              <IconH4 icon={RiStackLine}>Agent Usage</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Generalist:</span><span className={styles.statValue}>{analytics.agent_calls?.generalist ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Researcher:</span><span className={styles.statValue}>{analytics.agent_calls?.researcher ?? 0}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Evaluator:</span><span className={styles.statValue}>{analytics.agent_calls?.evaluator ?? 0}</span></div>
              </div>
            </div>

            <div className={styles.analyticsSection}>
              <IconH4 icon={RiStackLine}>Ask Usage</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Input Tokens:</span><span className={styles.statValue}>{analytics.tokens.input.toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Output Tokens:</span><span className={styles.statValue}>{analytics.tokens.output.toLocaleString()}</span></div>
                <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}><span className={styles.statLabel}>Total Tokens:</span><span className={styles.statValue}>{analytics.tokens.total.toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Router In:</span><span className={styles.statValue}>{(analytics.router_tokens?.input ?? 0).toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Router Out:</span><span className={styles.statValue}>{(analytics.router_tokens?.output ?? 0).toLocaleString()}</span></div>
              </div>
            </div>

            {Object.keys(analytics.model_usage || {}).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiStackLine}>Ask Models</IconH4>
                <div className={styles.modelList}>
                  {Object.entries(analytics.model_usage).map(([model, count]) => (
                    <div key={model} className={styles.modelItem}>
                      <span className={styles.modelName}>{model}</span>
                      <span className={styles.modelCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(analytics.provider_usage || {}).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiStackLine}>Ask Providers</IconH4>
                <div className={styles.providerList}>
                  {Object.entries(analytics.provider_usage).map(([provider, count]) => (
                    <div key={provider} className={styles.providerItem}>
                      <span className={styles.providerName}>{provider}</span>
                      <span className={styles.providerCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </>}

          {/* ── Swarm ── */}
          {showSwarm && swarmAnalytics && swarmAnalytics.total_sessions > 0 && <>
            <div className={styles.analyticsSection}>
              <IconH4 icon={RiTeamLine}>Swarm Usage</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Total Sessions:</span><span className={styles.statValue}>{swarmAnalytics.total_sessions}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Completed:</span><span className={styles.statValue}>{swarmAnalytics.completed}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Stopped:</span><span className={styles.statValue}>{swarmAnalytics.stopped}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Total Turns:</span><span className={styles.statValue}>{swarmAnalytics.total_turns}</span></div>
              </div>
            </div>

            {swarmAnalytics.tokens?.total > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiTeamLine}>Swarm Token Usage</IconH4>
                <div className={styles.analyticsStats}>
                  <div className={styles.stat}><span className={styles.statLabel}>Input Tokens:</span><span className={styles.statValue}>{swarmAnalytics.tokens.input.toLocaleString()}</span></div>
                  <div className={styles.stat}><span className={styles.statLabel}>Output Tokens:</span><span className={styles.statValue}>{swarmAnalytics.tokens.output.toLocaleString()}</span></div>
                  <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}><span className={styles.statLabel}>Total Tokens:</span><span className={styles.statValue}>{swarmAnalytics.tokens.total.toLocaleString()}</span></div>
                </div>
              </div>
            )}

            {Object.keys(swarmAnalytics.model_usage || {}).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiTeamLine}>Swarm Models</IconH4>
                <div className={styles.modelList}>
                  {Object.entries(swarmAnalytics.model_usage).map(([model, count]) => (
                    <div key={model} className={styles.modelItem}>
                      <span className={styles.modelName}>{model}</span>
                      <span className={styles.modelCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(swarmAnalytics.provider_usage || {}).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiTeamLine}>Swarm Providers</IconH4>
                <div className={styles.providerList}>
                  {Object.entries(swarmAnalytics.provider_usage).map(([provider, count]) => (
                    <div key={provider} className={styles.providerItem}>
                      <span className={styles.providerName}>{provider}</span>
                      <span className={styles.providerCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(swarmAnalytics.agent_participation || {}).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiTeamLine}>Swarm Participation</IconH4>
                <div className={styles.modelList}>
                  {Object.entries(swarmAnalytics.agent_participation).map(([agent, stats]) => (
                    <div key={agent} className={styles.modelItem} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                      <span className={styles.modelName} style={{ fontWeight: 'bold', textTransform: 'capitalize' }}>{agent}</span>
                      {stats && typeof stats === 'object' ? (
                        <>
                          <span style={{ fontSize: '0.8em', color: 'var(--text-secondary, #888)' }}>{stats.provider || '—'} / {stats.model || '—'}</span>
                          <span style={{ fontSize: '0.8em' }}>
                            ↑ {(stats.input_tokens || 0).toLocaleString()} in &nbsp; ↓{' '}
                            {(stats.output_tokens || 0).toLocaleString()} out &nbsp; (
                            {((stats.input_tokens || 0) + (stats.output_tokens || 0)).toLocaleString()} total)
                          </span>
                          <span style={{ fontSize: '0.8em', color: 'var(--text-secondary, #888)' }}>{stats.turns || 0} turns</span>
                        </>
                      ) : (
                        <span className={styles.modelCount}>{stats} turns</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>}

          {/* ── Workflow ── */}
          {showWorkflow && workflowAnalytics && workflowAnalytics.total_runs > 0 && <>
            <div className={styles.analyticsSection}>
              <IconH4 icon={RiStackLine}>Workflow Overview</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Total Runs:</span><span className={styles.statValue}>{workflowAnalytics.total_runs}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Completed:</span><span className={styles.statValue}>{workflowAnalytics.completed_runs}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Failed:</span><span className={styles.statValue}>{workflowAnalytics.failed_runs}</span></div>
              </div>
            </div>

            <div className={styles.analyticsSection}>
              <IconH4 icon={RiStackLine}>Workflow Token Usage</IconH4>
              <div className={styles.analyticsStats}>
                <div className={styles.stat}><span className={styles.statLabel}>Input Tokens:</span><span className={styles.statValue}>{_wfTok.input.toLocaleString()}</span></div>
                <div className={styles.stat}><span className={styles.statLabel}>Output Tokens:</span><span className={styles.statValue}>{_wfTok.output.toLocaleString()}</span></div>
                <div className={styles.stat} style={{ background: 'rgba(74,158,255,0.1)', borderRadius: '4px', fontWeight: 600 }}><span className={styles.statLabel}>Total Tokens:</span><span className={styles.statValue}>{_wfTok.total.toLocaleString()}</span></div>
              </div>
            </div>

            {Object.keys(workflowAnalytics.model_usage || {}).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiStackLine}>Workflow Models</IconH4>
                <div className={styles.modelList}>
                  {Object.entries(workflowAnalytics.model_usage).map(([model, count]) => (
                    <div key={model} className={styles.modelItem}>
                      <span className={styles.modelName}>{model}</span>
                      <span className={styles.modelCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(workflowAnalytics.provider_usage || {}).length > 0 && (
              <div className={styles.analyticsSection}>
                <IconH4 icon={RiStackLine}>Workflow Providers</IconH4>
                <div className={styles.providerList}>
                  {Object.entries(workflowAnalytics.provider_usage).map(([provider, count]) => (
                    <div key={provider} className={styles.providerItem}>
                      <span className={styles.providerName}>{provider}</span>
                      <span className={styles.providerCount}>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </>}

          {/* ── Unified Agent Usage (Ask + Workflow, one card per agent type) ── */}
          {showAgents && agentCards.length > 0 && agentCards.map((card) => (
            <div key={card.key} className={styles.analyticsSection}>
              <IconH4 icon={RiTeamLine}>{card.label}</IconH4>
              <div className={styles.modelList}>
                {/* Total header */}
                <div className={styles.modelItem} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '2px', marginBottom: 4 }}>
                  <span style={{ fontSize: '0.8em', fontWeight: 600 }}>
                    ↑ {card.totalIn.toLocaleString()} in &nbsp; ↓ {card.totalOut.toLocaleString()} out &nbsp;
                    <span style={{ color: 'var(--text-secondary, #888)' }}>
                      ({(card.totalIn + card.totalOut).toLocaleString()} total)
                    </span>
                  </span>
                  {/* Mode breakdown */}
                  {card.askIn > 0 && (
                    <span style={{ fontSize: '0.75em', color: 'var(--text-secondary, #888)' }}>
                      Ask &nbsp; ↑ {card.askIn.toLocaleString()} in &nbsp; ↓ {card.askOut.toLocaleString()} out
                    </span>
                  )}
                  {card.wfIn > 0 && (
                    <span style={{ fontSize: '0.75em', color: 'var(--text-secondary, #888)' }}>
                      Workflow &nbsp; ↑ {card.wfIn.toLocaleString()} in &nbsp; ↓ {card.wfOut.toLocaleString()} out
                      {card.wfRuns > 0 && ` (${card.wfRuns} node run${card.wfRuns !== 1 ? 's' : ''})`}
                    </span>
                  )}
                </div>
                {/* Per-sub-agent rows */}
                {Object.entries(card.subAgents).map(([subName, sub]) => (
                  <div key={subName} className={styles.modelItem} style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '1px', paddingLeft: 12, borderLeft: '2px solid var(--border-color, #ddd)', marginLeft: 4, marginTop: 4 }}>
                    <span className={styles.modelName} style={{ textTransform: 'capitalize' }}>{subName}</span>
                    <span style={{ fontSize: '0.8em', color: 'var(--text-secondary, #888)' }}>
                      {sub.provider || '—'} / {sub.model || '—'}
                    </span>
                    <span style={{ fontSize: '0.8em' }}>
                      ↑ {(sub.input_tokens || 0).toLocaleString()} in &nbsp; ↓ {(sub.output_tokens || 0).toLocaleString()} out
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {showWorkflow && (!workflowAnalytics || workflowAnalytics.total_runs === 0) && (
            <div className={styles.analyticsSection}>
              <IconH4 icon={RiStackLine}>Workflow</IconH4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary, #888)', margin: 0 }}>
                No workflow runs yet. Build and run a workflow to see usage here.
              </p>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
